/**
 * Copies of a whole project for another Pro account, from the client
 * (netlify/functions/api/project-copies.ts). Sending asks; the person named accepts, and the
 * copy is made on the server in slices, this side asking for the next one until it is done.
 */
import { apiClient } from './apiClient';

export type ProjectCopyStatus = 'pending' | 'copying' | 'done' | 'declined' | 'cancelled' | 'expired';

export interface ProjectCopyProgress {
    copiedWorkspaces: number;
    totalWorkspaces: number;
    copiedFiles: number;
    missingFiles: number;
    bytes: number;
}

export interface ProjectCopy {
    id: string;
    projectId: string;
    projectName: string;
    fromName: string;
    fromEmail?: string;
    toEmail: string;
    status: ProjectCopyStatus;
    folderCount: number;
    workspaceCount: number;
    createdAt: number;
    updatedAt?: number;
    expiresAt?: number;
    progress: ProjectCopyProgress | null;
    /** The copy's project in the recipient's library, once done */
    newProjectId?: string;
    error?: string;
}

export interface ProjectCopies {
    /** Sent by the signed-in person, newest first */
    sent: ProjectCopy[];
    /** Waiting for the signed-in person's answer, or being made */
    received: ProjectCopy[];
}

/** How long to wait before asking again while a slice is busy or has just failed */
const RETRY_WAIT_MS = 1500;
const MAX_FAILED_SLICES = 4;

const wait = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** What an API refusal says, for the page */
export const copyErrorText = (e: unknown, fallback: string): string => {
    const data = (e as { data?: { error?: unknown; message?: unknown } })?.data;
    const said = data?.error ?? data?.message ?? (e as { message?: unknown })?.message;
    return typeof said === 'string' && said && !/^Request failed/.test(said) ? said : fallback;
};

/** The owner sends a copy of a project to an address */
export const sendProjectCopy = async (projectId: string, email: string): Promise<ProjectCopy> =>
    (await apiClient.post<{ copy: ProjectCopy }>('/project-copies', { projectId, email })).copy;

export const listProjectCopies = async (): Promise<ProjectCopies> => {
    const result = await apiClient.get<Partial<ProjectCopies>>('/project-copies');
    return { sent: Array.isArray(result?.sent) ? result.sent : [], received: Array.isArray(result?.received) ? result.received : [] };
};

export const declineProjectCopy = async (copyId: string): Promise<ProjectCopy> =>
    (await apiClient.post<{ copy: ProjectCopy }>('/project-copies/decline', { copyId })).copy;

/** The sender takes back a copy nobody has accepted yet */
export const cancelProjectCopy = async (copyId: string): Promise<ProjectCopy> =>
    (await apiClient.delete<{ copy: ProjectCopy }>('/project-copies', { data: { copyId } })).copy;

/**
 * Accepts a copy and sees it through: the server makes it slice by slice, and each slice's
 * progress is reported. Resolves with the finished copy (its newProjectId names the project).
 */
export const acceptProjectCopy = async (copyId: string, onProgress?: (copy: ProjectCopy) => void): Promise<ProjectCopy> => {
    let copy = (await apiClient.post<{ copy: ProjectCopy }>('/project-copies/accept', { copyId })).copy;
    let failures = 0;
    while (copy.status === 'copying') {
        onProgress?.(copy);
        try {
            copy = (await apiClient.post<{ copy: ProjectCopy }>('/project-copies/continue', { copyId })).copy;
            failures = 0;
        } catch (e) {
            const status = (e as { status?: number })?.status;
            const retry = (e as { data?: { busy?: boolean; retry?: boolean } })?.data;
            if (status === 409 && retry?.busy) { await wait(RETRY_WAIT_MS); continue; }
            if ((status === 500 && retry?.retry) || status === 0 || status === 502 || status === 503 || status === 504) {
                if (++failures > MAX_FAILED_SLICES) throw e;
                await wait(RETRY_WAIT_MS * failures);
                continue;
            }
            throw e;
        }
    }
    onProgress?.(copy);
    return copy;
};
